import { useFetcher, useRouteLoaderData } from "@remix-run/react";
import {
  getCurrencyForCountry,
  getUnlimitedSecondBuyPlan,
} from "~/routes/api.get-products";
import { loader as _appLoader } from "~/routes/_app";

// Shared "reward screen" for the unlimited offer. Rendered inside a
// <Modal subtleClose> by both the feed banner and the in-chat header teaser,
// so the claim flow + copy live in one place. The full-view PNG is the payoff;
// tapping it (or the button) routes to /process-payment.
export default function UnlimitedOfferContent() {
  const routeData = useRouteLoaderData<typeof _appLoader>("routes/_app");
  const defaultPaymentProvider = routeData?.defaultPaymentProvider as string;
  const countryCode = routeData?.userProfile?.country_code || "US";
  const plan =
    getUnlimitedSecondBuyPlan(getCurrencyForCountry(countryCode)) ??
    getUnlimitedSecondBuyPlan("USD");

  // Each creative has its localized price baked in, so pick the image that
  // matches the user's plan currency. USD covers every other market (they're
  // charged USD anyway). Keep this list in sync with the public/ assets.
  const BANNER_CURRENCIES = ["usd", "eur", "gbp", "aud", "cad", "nzd"];
  const bannerCurrency =
    plan && BANNER_CURRENCIES.includes(plan.currency) ? plan.currency : "usd";
  const bannerSrc = `/unlimited-banner-${bannerCurrency}.png`;

  const fetcher = useFetcher<{ error?: string | null }>();
  const isProcessing = fetcher.state !== "idle";

  const claim = () => {
    if (!plan) return;
    const fd = new FormData();
    fd.set("planId", plan.id);
    fd.set("currency", plan.currency);
    fd.set("provider", defaultPaymentProvider);
    fd.set(
      "redirectTo",
      typeof window !== "undefined" ? window.location.pathname : "",
    );
    fetcher.submit(fd, { method: "post", action: "/process-payment" });
  };

  return (
    <div className="flex flex-col items-center p-4 md:p-6 max-w-[640px]">
      {/* Reward-screen header — this reads as "you earned this". */}
      <div className="text-center mb-3">
        <div className="text-xl md:text-2xl font-black uppercase tracking-wide text-yellow-400 drop-shadow-[0_0_12px_rgba(234,179,8,0.45)]">
          🎁 Bonus Unlocked
        </div>
        <div className="text-sm md:text-base font-semibold text-text mt-1">
          🔓 Unlocked after your first purchase
        </div>
        <div className="text-xs text-text opacity-60 mt-0.5">
          Available once per account
        </div>
      </div>

      <button
        type="button"
        onClick={claim}
        disabled={isProcessing || !plan}
        aria-label="Claim the offer"
        className="block w-full cursor-pointer rounded-2xl overflow-hidden border-2 border-red-500/60
                   shadow-[0_0_30px_rgba(255,0,0,0.3)] transition-transform active:scale-[0.99]
                   disabled:opacity-60"
      >
        <img
          src={bannerSrc}
          alt="Send unlimited messages for 1 hour"
          className="block w-full h-auto select-none"
          draggable={false}
        />
      </button>

      <button
        type="button"
        onClick={claim}
        disabled={isProcessing || !plan}
        className="mt-4 w-full rounded-full bg-gradient-to-r from-red-500 to-red-700 text-white
                   font-black uppercase tracking-wider py-3 text-base shadow-lg
                   transition-transform active:scale-[0.99] disabled:opacity-60"
      >
        {isProcessing ? "Processing…" : "Claim Offer"}
      </button>
      {/* Real scarcity — no countdown, no fake urgency. */}
      <p className="mt-2 text-xs text-text opacity-60 text-center">
        One-time offer — it's gone after your next purchase.
      </p>
    </div>
  );
}
